import React, { useMemo } from "react";
import "./QuestionGridNumber.css";
import useAppHooks from "../../../../shared/hooks/useAppHooks";
import { getExamSectionKey } from "../../../../shared/utilities/helpers/examSectionKey";

export default function QuestionGridNumber({ selectedIndex, onSelect, questions = null }) {
  const { selector } = useAppHooks();
  const { questionsData } = selector((s) => s.examSlice);
  const { practiceSolution } = selector((s) => s.analyticsSlice);

  const analyticsData = practiceSolution?.data || [];

  const data = useMemo(() => {
    if (Array.isArray(questions) && questions.length > 0) {
      return questions;
    }

    return questionsData.map((q) => {
      const analyticsQ = analyticsData.find(
        (a) => String(a.qid) === String(q.qid)
      );

      return {
        ...q,
        is_correct: analyticsQ?.is_correct,
        correct: analyticsQ?.correct,
      };
    });
  }, [analyticsData, questions, questionsData]);

  const pickValue = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return null;
  };

  const getQuestionOptions = (q) =>
    Array.isArray(q?.option) ? q.option : [];

  const getValidOptionIds = (q) =>
    new Set(getQuestionOptions(q).map((opt) => String(opt?.option_id)));

  const hasExplicitAttemptFlag = (q) => {
    const flag = pickValue(
      q?.is_attempted,
      q?.isAttempted,
      q?.attempted,
      q?.is_answered
    );

    if (flag === true || flag === 1) return true;
    if (flag === false || flag === 0) return false;

    const normalized = String(flag ?? "")
      .trim()
      .toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;

    return false;
  };

  const getSelectedOptionIds = (q) => {
    const validOptionIds = getValidOptionIds(q);
    const selectedRaw = pickValue(
      q?.selectedOption,
      q?.selected_option,
      q?.user_selected_option,
      q?.selected_option_id,
      q?.user_option_id
    );

    let selectedIds = [];
    if (Array.isArray(selectedRaw)) {
      selectedIds = selectedRaw.map((id) => String(id));
    } else if (
      selectedRaw !== null &&
      selectedRaw !== undefined &&
      String(selectedRaw).trim() !== ""
    ) {
      selectedIds = [String(selectedRaw)];
    }

    return selectedIds.filter((id) => validOptionIds.has(id));
  };

  const getExamIntegerAnswer = (q) =>
    String(
      pickValue(
        q?.integerAnswer,
        q?.integer_Answer,
        q?.integer_answer,
        ""
      )
    ).trim();

  const getAnalyticsIntegerAnswer = (q) =>
    String(
      pickValue(
        q?.option_int,
        q?.user_option_int,
        q?.user_answer_int,
        q?.attempted_answer,
        q?.answer_given,
        ""
      )
    ).trim();

  const hasOptionsList = (q) => q?.option != null && Array.isArray(q.option);

  const getUserIntegerAnswer = (q) => {
    if (!hasExplicitAttemptFlag(q)) return "";

    return getAnalyticsIntegerAnswer(q) || getExamIntegerAnswer(q);
  };

  const getCorrectIntegerAnswer = (q) =>
    String(
      pickValue(q?.answer_int, q?.answer, q?.correct_answer, q?.answer_text, "")
    ).trim();

  const compareIntegerAnswers = (userAnswer, correctAnswer) => {
    if (!userAnswer || !correctAnswer) return false;
    const userNum = Number(userAnswer);
    const correctNum = Number(correctAnswer);
    if (Number.isFinite(userNum) && Number.isFinite(correctNum)) {
      return userNum === correctNum;
    }
    return userAnswer === correctAnswer;
  };

  const getMcqStatus = (q) => {
    const answerType = Number(q?.answer_type_id);
    const options = getQuestionOptions(q);
    if (!options.length) return null;

    const analyticsSelected = options.filter(
      (opt) => Number(opt?.is_user_correct_ans) === 1
    );

    if (analyticsSelected.length > 0) {
      const isCorrect = analyticsSelected.every(
        (opt) => Number(opt?.is_correct) === 1
      );
      return isCorrect ? "correct" : "incorrect";
    }

    const selectedIds = getSelectedOptionIds(q);
    if (!selectedIds.length) return null;

    const correctIds = options
      .filter((opt) => Number(opt?.is_correct) === 1)
      .map((opt) => String(opt.option_id));

    if (answerType === 2) {
      const selectedSet = new Set(selectedIds);
      const correctSet = new Set(correctIds);
      const isCorrect =
        selectedSet.size === correctSet.size &&
        [...correctSet].every((id) => selectedSet.has(id));
      return isCorrect ? "correct" : "incorrect";
    }

    const isCorrect = options.some(
      (opt) =>
        selectedIds.includes(String(opt.option_id)) &&
        Number(opt?.is_correct) === 1
    );
    return isCorrect ? "correct" : "incorrect";
  };

  const getUserAnswerStatus = (q) => {
    const userAnswer = String(
      pickValue(q?.user_answer, q?.your_answer, "")
    ).toUpperCase();
    if (userAnswer === "RIGHT" || userAnswer === "CORRECT") return "correct";
    if (userAnswer === "WRONG" || userAnswer === "INCORRECT") return "incorrect";
    return null;
  };

  const getIntegerStatus = (q) => {
    const userAnswer = getUserIntegerAnswer(q);
    if (!userAnswer) return null;

    return compareIntegerAnswers(userAnswer, getCorrectIntegerAnswer(q))
      ? "correct"
      : "incorrect";
  };

  // Keep grid classification in sync with QuestionText rendering source of truth.
  const getQuestionStatus = (q) => {
    const mcqStatus = getMcqStatus(q);
    if (mcqStatus) return mcqStatus;

    const userAnswerStatus = getUserAnswerStatus(q);
    if (userAnswerStatus) return userAnswerStatus;

    // Ignore analytics `user_answer: WRONG` unless the backend also marks the question attempted.
    if (
      !hasOptionsList(q) &&
      hasExplicitAttemptFlag(q) &&
      String(pickValue(q?.user_answer, q?.your_answer, "")).toUpperCase() ===
        "WRONG"
    ) {
      return "incorrect";
    }

    const integerStatus = getIntegerStatus(q);
    if (integerStatus) return integerStatus;

    return "unattempted";
  };

  // Group data by subject & section
  const groupedData = useMemo(() => {
    if (!data.length) return [];
    const sections = [];
    let currentSection = null;
    data.forEach((q) => {
      const key = getExamSectionKey(q) || "Unknown";
      if (
        !currentSection ||
        currentSection.key !== key ||
        q.display_number === 1
      ) {
        currentSection = {
          key,
          heading: getExamSectionKey(q) || "Unknown",
          questions: [],
        };
        sections.push(currentSection);
      }
      currentSection.questions.push(q);
    });
    return sections;
  }, [data]);


  const statusCounts = useMemo(() => {
  const counts = {
    correct: 0,
    incorrect: 0,
    unattempted: 0,
    marked: 0,
  };

  data.forEach((q) => {
    if (q.marked) {
      counts.marked++;
      return;
    }

    const status = getQuestionStatus(q);
    if (status === "correct") counts.correct++;
    else if (status === "incorrect") counts.incorrect++;
    else counts.unattempted++;
  });

  return counts;
}, [data]);

  if (!data.length)
    return <p style={{ textAlign: "center" }}>No questions found</p>;

  let globalIndex = 0;

//   const getClass = (q, globalI) => {
//   const isSelected = globalI === selectedIndex;

//   if (isSelected) return "selected";
//   if (q.marked) return "marked";

//   const attempted =
//     (q.selectedOption !== null && q.selectedOption !== undefined) ||
//     (q.integerAnswer && String(q.integerAnswer).trim() !== "");

//   if (!attempted) return "unattempted";

//   let isCorrect = false;

//   if (q.option && Array.isArray(q.option)) {
//     q.option.forEach((opt) => {
//       if (
//         String(opt.option_id) === String(q.selectedOption) &&
//         opt.is_correct === 1
//       ) {
//         isCorrect = true;
//       }
//     });
//   }

//   return isCorrect ? "correct" : "incorrect";
// };

  const getClass = (q) => {
    if (q.marked) return "marked";
    return getQuestionStatus(q);
  };

  return (
    <div className="questionGrid_wrapper">
      <h4 className="grid_heading">Your Response Grid</h4>
       <h4 className="grid_overview">overview</h4>

      {/* Status Legend with Counts Inside Circles */}
      <div className="overview">
        <div className="status_row">
          <span className="status_circle correct">{statusCounts.correct}</span>
          Correct
        </div>
        <div className="status_row">
          <span className="status_circle incorrect">{statusCounts.incorrect}</span>
          Incorrect
        </div>
        <div className="status_row">
          <span className="status_circle unattempted">{statusCounts.unattempted}</span>
          Unattempted
        </div>
        <div className="status_row">
          <span className="status_circle marked">{statusCounts.marked}</span>
          Marked for Review
        </div>
      </div>

      {/* Sections and Questions */}
      {groupedData.map((section, sectionIndex) => (
        <div key={sectionIndex} className="section_wrapper">
          <h5>{section.heading}</h5>
          <div className="grid">
            {section.questions.map((q) => {
              
              const index = globalIndex++;
              const className = getClass(q, index);

              const questionNumber =index+1; 
              return (
                <div
                  key={index}
                  className={`grid_item ${className}`}
                  onClick={() => onSelect(index)}
                  title={`Q${questionNumber}`}
                >
                  {questionNumber}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}